import "./microphone-panel.css";
import {
  createMicrophoneCaptureController,
  type MicrophoneCaptureController,
  type MicrophoneCaptureSnapshot,
  type MicrophoneRecording,
} from "./microphone-capture";

export interface MicrophonePanelCopy {
  title: string;
  detail: string;
  tone: "neutral" | "working" | "ready" | "recording" | "success" | "error";
}

export interface MicrophonePanelOptions {
  controller?: MicrophoneCaptureController;
  document?: Document;
  window?: Window;
  objectUrlApi?: Pick<typeof URL, "createObjectURL" | "revokeObjectURL">;
}

export interface MountedMicrophonePanel {
  element: HTMLElement;
  controller: MicrophoneCaptureController;
  expand(): void;
  collapse(): void;
  dispose(): void;
}

export function formatMicrophoneDuration(milliseconds: number): string {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1_000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

export function microphonePanelCopy(
  snapshot: MicrophoneCaptureSnapshot,
): MicrophonePanelCopy {
  switch (snapshot.status) {
    case "unsupported":
      return {
        title: "마이크 사용 불가",
        detail:
          snapshot.errorMessage ??
          "HTTPS 연결과 마이크 녹음을 지원하는 브라우저가 필요합니다.",
        tone: "error",
      };
    case "requesting_permission":
      return {
        title: "마이크 권한 확인 중",
        detail: "브라우저의 마이크 사용 허용 창에서 허용을 선택하십시오.",
        tone: "working",
      };
    case "ready":
      return {
        title: "마이크 연결 완료",
        detail: "아직 녹음되지 않습니다. 녹음 시작을 눌러야 음성이 수집됩니다.",
        tone: "ready",
      };
    case "starting":
      return {
        title: "녹음 준비 중",
        detail: "마이크 장치와 안전한 녹음 형식을 확인하고 있습니다.",
        tone: "working",
      };
    case "recording":
      return {
        title: "녹음 중",
        detail: "진료실 음성을 기기 메모리에만 임시 수집하고 있습니다.",
        tone: "recording",
      };
    case "stopping":
      return {
        title: "녹음 종료 중",
        detail: "마지막 오디오 조각을 정리하고 마이크 연결을 해제하고 있습니다.",
        tone: "working",
      };
    case "recorded":
      return {
        title: "녹음 완료",
        detail: "서버 전송이나 차트 저장은 아직 수행되지 않았습니다.",
        tone: "success",
      };
    case "denied":
      return {
        title: "마이크 권한 거부됨",
        detail:
          snapshot.errorMessage ??
          "삼성 인터넷의 사이트 권한에서 마이크를 허용한 뒤 다시 연결하십시오.",
        tone: "error",
      };
    case "error":
      return {
        title: "마이크 오류",
        detail: snapshot.errorMessage ?? "마이크 연결 또는 녹음 중 오류가 발생했습니다.",
        tone: "error",
      };
    case "idle":
    default:
      return {
        title: "마이크 연결 전",
        detail: "마이크 연결 버튼을 눌러야 브라우저 권한 요청이 시작됩니다.",
        tone: "neutral",
      };
  }
}

function escapeHtml(value: unknown): string {
  return String(value ?? "").replace(
    /[&<>"']/g,
    (character) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[character] ?? character,
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1_024) return `${bytes} B`;
  if (bytes < 1_024 * 1_024) return `${(bytes / 1_024).toFixed(1)} KB`;
  return `${(bytes / (1_024 * 1_024)).toFixed(1)} MB`;
}

function actionButtons(snapshot: MicrophoneCaptureSnapshot): string {
  switch (snapshot.status) {
    case "idle":
    case "denied":
    case "error":
      return `<button type="button" class="microphone-primary" data-microphone-action="connect">마이크 연결</button>`;
    case "unsupported":
      return `<button type="button" class="microphone-primary" disabled>마이크 사용 불가</button>`;
    case "requesting_permission":
    case "starting":
    case "stopping":
      return `<button type="button" class="microphone-primary" disabled>처리 중</button>`;
    case "ready":
      return `
        <button type="button" class="microphone-primary" data-microphone-action="start">녹음 시작</button>
        <button type="button" class="microphone-secondary" data-microphone-action="disconnect">연결 해제</button>`;
    case "recording":
      return `<button type="button" class="microphone-stop" data-microphone-action="stop">녹음 중지</button>`;
    case "recorded":
      return `<button type="button" class="microphone-secondary" data-microphone-action="discard">녹음 폐기</button>`;
  }
}

function recordingSummary(
  snapshot: MicrophoneCaptureSnapshot,
  recording: MicrophoneRecording | null,
): string {
  if (snapshot.status === "recording" || snapshot.status === "stopping") {
    return `
      <div class="microphone-meter" aria-label="녹음 시간">
        <span class="microphone-recording-dot" aria-hidden="true"></span>
        <strong data-microphone-duration>${formatMicrophoneDuration(snapshot.durationMs)}</strong>
      </div>`;
  }
  if (snapshot.status === "recorded" && recording) {
    return `
      <div class="microphone-result">
        <dl>
          <div><dt>길이</dt><dd>${formatMicrophoneDuration(recording.durationMs)}</dd></div>
          <div><dt>크기</dt><dd>${formatBytes(recording.blob.size)}</dd></div>
          <div><dt>형식</dt><dd>${escapeHtml(recording.mimeType)}</dd></div>
        </dl>
        <audio controls preload="metadata" data-microphone-preview aria-label="녹음 확인 재생"></audio>
      </div>`;
  }
  return "";
}

export function mountMicrophonePanel(
  options: MicrophonePanelOptions = {},
): MountedMicrophonePanel {
  const documentRef = options.document ?? document;
  const windowRef = options.window ?? window;
  const objectUrlApi = options.objectUrlApi ?? globalThis.URL;
  const controller = options.controller ?? createMicrophoneCaptureController();
  const existing = documentRef.querySelector<HTMLElement>("#lys-microphone-panel");
  if (existing) throw new Error("Microphone panel is already mounted.");

  const host = documentRef.createElement("aside");
  host.id = "lys-microphone-panel";
  host.className = "microphone-panel-host";
  host.setAttribute("aria-label", "실제 마이크 녹음 도구");
  documentRef.body.append(host);

  let expanded = false;
  let disposed = false;
  let objectUrl: string | null = null;
  let timerId: number | null = null;

  function revokeObjectUrl(): void {
    if (!objectUrl) return;
    objectUrlApi.revokeObjectURL(objectUrl);
    objectUrl = null;
  }

  function syncTimer(snapshot: MicrophoneCaptureSnapshot): void {
    if (snapshot.status === "recording" && timerId === null) {
      timerId = windowRef.setInterval(() => {
        const duration = host.querySelector<HTMLElement>("[data-microphone-duration]");
        if (duration) {
          duration.textContent = formatMicrophoneDuration(
            controller.getSnapshot().durationMs,
          );
        }
      }, 250);
      return;
    }
    if (snapshot.status !== "recording" && timerId !== null) {
      windowRef.clearInterval(timerId);
      timerId = null;
    }
  }

  function render(snapshot = controller.getSnapshot()): void {
    const copy = microphonePanelCopy(snapshot);
    const recording = controller.getRecording();
    syncTimer(snapshot);

    host.innerHTML = `
      <button type="button" class="microphone-launcher tone-${copy.tone}"
        data-microphone-action="toggle" aria-expanded="${expanded}">
        <span class="microphone-launcher-icon" aria-hidden="true">🎙</span>
        <span class="microphone-launcher-copy">
          <strong>실제 마이크</strong>
          <small>${escapeHtml(copy.title)}</small>
        </span>
        <span class="microphone-status-dot" aria-hidden="true"></span>
      </button>
      ${
        expanded
          ? `<section class="microphone-sheet" aria-labelledby="microphone-panel-title">
              <header>
                <div>
                  <span class="microphone-eyebrow">DEVICE AUDIO · LOCAL ONLY</span>
                  <h2 id="microphone-panel-title">실제 마이크 연결</h2>
                </div>
                <button type="button" class="microphone-close" data-microphone-action="collapse"
                  aria-label="마이크 패널 닫기">×</button>
              </header>
              <div class="microphone-state tone-${copy.tone}" role="status" aria-live="polite">
                <strong>${escapeHtml(copy.title)}</strong>
                <p>${escapeHtml(copy.detail)}</p>
              </div>
              ${recordingSummary(snapshot, recording)}
              <div class="microphone-actions">${actionButtons(snapshot)}</div>
              <p class="microphone-privacy">
                버튼을 눌러야 녹음됩니다. 현재 단계에서는 음성이 서버·차트·D1·로그로 전송되거나 저장되지 않습니다.
              </p>
            </section>`
          : ""
      }`;

    if (recording && snapshot.status === "recorded") {
      revokeObjectUrl();
      const nextObjectUrl = objectUrlApi.createObjectURL(recording.blob);
      objectUrl = nextObjectUrl;
      const preview = host.querySelector<HTMLAudioElement>("[data-microphone-preview]");
      if (preview) preview.src = nextObjectUrl;
    }
  }

  async function handleAction(action: string): Promise<void> {
    switch (action) {
      case "toggle":
        expanded = !expanded;
        render();
        return;
      case "collapse":
        expanded = false;
        render();
        return;
      case "connect":
        expanded = true;
        await controller.connect();
        return;
      case "start":
        revokeObjectUrl();
        await controller.start();
        return;
      case "stop": {
        const recording = await controller.stop();
        windowRef.dispatchEvent(
          new CustomEvent<MicrophoneRecording>("lys:microphone-recording-ready", {
            detail: recording,
          }),
        );
        return;
      }
      case "discard":
        revokeObjectUrl();
        controller.discard();
        return;
      case "disconnect":
        controller.disconnect();
        return;
      default:
        return;
    }
  }

  const unsubscribe = controller.subscribe((snapshot) => render(snapshot));
  const clickListener = (event: Event): void => {
    const target = (event.target as Element | null)?.closest<HTMLElement>(
      "[data-microphone-action]",
    );
    if (!target || !host.contains(target)) return;
    const action = target.dataset.microphoneAction;
    if (!action) return;
    void handleAction(action).catch(() => {
      render(controller.getSnapshot());
    });
  };
  host.addEventListener("click", clickListener);

  const beforeUnloadListener = (): void => {
    controller.dispose();
    revokeObjectUrl();
  };
  windowRef.addEventListener("beforeunload", beforeUnloadListener);
  render();

  return {
    element: host,
    controller,
    expand() {
      expanded = true;
      render();
    },
    collapse() {
      expanded = false;
      render();
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      if (timerId !== null) windowRef.clearInterval(timerId);
      timerId = null;
      unsubscribe();
      controller.dispose();
      revokeObjectUrl();
      host.removeEventListener("click", clickListener);
      windowRef.removeEventListener("beforeunload", beforeUnloadListener);
      host.remove();
    },
  };
}
